/* PnConfigProvider — bridges the PreNumbers design tokens into Ant Design.

   The app's look is driven by the --pn-* CSS variables in
   styles/colors_and_type.css, which swap between dark/light whenever the
   ThemeToggle flips the document's [data-theme] attribute. AntD's own
   components (Modal, message, Dropdown, ...) don't read those variables, so
   left alone they render in stock AntD blue-on-white regardless of theme.

   This provider reads the resolved --pn-* values straight off the document,
   maps them onto AntD's theme tokens, and re-builds the config whenever
   [data-theme] changes — so AntD surfaces follow the same color theme and the
   light/dark toggle for free. Wrap the app once with it (see App.jsx). */

import { useState, useEffect, useMemo } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';

function getThemeMode() {
  if (typeof document === 'undefined') return 'dark';
  return document.documentElement.getAttribute('data-theme') || 'dark';
}

/* Pull the live, resolved value of a CSS custom property off :root. Because we
   read computed style, this already reflects whichever [data-theme] block won. */
function readTokens() {
  const cs = getComputedStyle(document.documentElement);
  const v = (name) => cs.getPropertyValue(name).trim();
  return {
    fgPrimary:    v('--pn-fg-primary'),
    fgSecondary:  v('--pn-fg-secondary'),
    fgTertiary:   v('--pn-fg-tertiary'),
    fgQuaternary: v('--pn-fg-quaternary'),
    bgPage:       v('--pn-bg-page'),
    bgPanel:      v('--pn-bg-panel'),
    bgSurface:    v('--pn-bg-surface'),
    bgRaised:     v('--pn-bg-raised'),
    border:       v('--pn-border-solid-2'),
    borderSubtle: v('--pn-border-solid-1'),
    teal:         v('--pn-brand-teal'),
    tealHover:    v('--pn-brand-teal-hover'),
    success:      v('--pn-success'),
    warning:      v('--pn-warning'),
    danger:       v('--pn-danger'),
    overlay:      v('--pn-overlay'),
    fontSans:     v('--pn-font-sans'),
  };
}

export function PnConfigProvider({ children }) {
  const [mode, setMode] = useState(getThemeMode);

  // Re-theme AntD when the global [data-theme] attribute flips.
  useEffect(() => {
    const obs = new MutationObserver(() => setMode(getThemeMode()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  const themeConfig = useMemo(() => {
    const t = readTokens();
    const isDark = mode !== 'light';
    return {
      // Seed AntD's palette generation with our base bg/fg so derived tokens
      // (hovers, disabled states, borders) land in the right tonal range.
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary:   t.teal,
        colorInfo:      t.teal,
        colorSuccess:   t.success,
        colorWarning:   t.warning,
        colorError:     t.danger,
        colorLink:      t.teal,
        colorLinkHover: t.tealHover,

        colorTextBase: t.fgPrimary,
        colorText:           t.fgPrimary,
        colorTextSecondary:  t.fgSecondary,
        colorTextTertiary:   t.fgTertiary,
        colorTextQuaternary: t.fgQuaternary,

        colorBgBase:     isDark ? t.bgPanel : '#ffffff',
        colorBgElevated: t.bgSurface, // Modal / Dropdown / message / Popover surface
        colorBgMask:     t.overlay,   // dialog backdrop

        colorBorder:          t.border,
        colorBorderSecondary: t.borderSubtle,

        borderRadius: 8,
        fontFamily:   t.fontSans,
      },
      components: {
        Modal: {
          contentBg:  t.bgSurface,
          headerBg:   t.bgSurface,
          footerBg:   'transparent',
          titleColor: t.fgPrimary,
        },
        Message: {
          contentBg: t.bgSurface,
        },
        Notification: {
          colorBgElevated: t.bgSurface,
        },
        Dropdown: {
          colorBgElevated: t.bgSurface,
        },
      },
    };
  }, [mode]);

  return <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>;
}

export default PnConfigProvider;
